import React, { useState } from 'react';
import { ActionIcon, Badge, Box, Button, Group, Stepper, Text, Tooltip } from '@mantine/core';
import { IconArrowLeft, IconArrowRight, IconCalculator, IconDeviceFloppy, IconFileTypePdf, IconX } from '@tabler/icons-react';
import type { ITablaEditable } from '../../../globales/componentes/tabla/useEdicionFilas';
import type { IConceptoCostoPostventa, ICotizacionPostventa, IRepuestoPostventa } from '../../../modelos/CotizadorPostventa/entidades/ICotizacionPostventa';
import type { CampoCotizacionPostventa, SeccionConceptos } from '../interfaces';
import { PasoDatosGeneralesVista } from './stepper/PasoDatosGeneralesVista';
import { PasoRepuestosVista } from './stepper/PasoRepuestosVista';
import { PasoCostosVista } from './stepper/PasoCostosVista';
import { PasoPrecioVista } from './stepper/PasoPrecioVista';

interface CrearCotizacionPostventaVistaProps {
    cotizacion: ICotizacionPostventa;
    repuestos: ITablaEditable<IRepuestoPostventa>;
    conceptos: Record<SeccionConceptos, ITablaEditable<IConceptoCostoPostventa>>;
    incoterms: { value: string; label: string }[];
    errores: Record<string, string>;
    hayFilaEnEdicion: boolean;
    requiereRecalculo: boolean;
    alCambiar: (campo: CampoCotizacionPostventa, valor: string | number) => void;
    /** Valida el paso indicado y marca los errores; true si se puede avanzar. */
    validarPaso: (paso: number) => boolean;
    alCalcular: () => void;
    alGenerarPdf: () => void;
    alGuardar: () => void;
    alVolver: () => void;
}

const ULTIMO_PASO = 3;

/** Encabezado, pasos y navegación de la cotización postventa. El contenido de cada paso es su propia vista. */
export const CrearCotizacionPostventaVista: React.FC<CrearCotizacionPostventaVistaProps> = (props) => {
    const { cotizacion, hayFilaEnEdicion, requiereRecalculo } = props;
    const [paso, setPaso] = useState(0);

    const irAPaso = (destino: number) => {
        // Hacia atrás siempre se puede; hacia adelante se validan los pasos intermedios.
        for (let p = paso; p < destino; p++) if (!props.validarPaso(p)) return;
        setPaso(destino);
    };

    const sinResultado = !cotizacion.Resultado || requiereRecalculo;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '20px' }}>
            <Group justify="space-between" mb="md">
                <div>
                    <Group gap="xs">
                        <Text fw={700} size="lg">{cotizacion.Id ? cotizacion.Proyecto || 'Cotización Postventa' : 'Nueva Cotización Postventa'}</Text>
                        <Badge variant="light" size="sm" radius="xl" style={{ textTransform: 'none' }}>{cotizacion.Estado?.Nombre ?? 'Borrador'}</Badge>
                    </Group>
                    <Text c="dimmed" size="xs">Modernizaciones (MOD QR) y servicios especiales de ingeniería (SEB)</Text>
                </div>
                <Tooltip label="Volver al listado" withinPortal style={{ fontSize: '10px', padding: '3px 5px' }}>
                    <ActionIcon variant="subtle" color="gray" radius="xl" onClick={props.alVolver}>
                        <IconX size={18} />
                    </ActionIcon>
                </Tooltip>
            </Group>

            <Stepper active={paso} onStepClick={hayFilaEnEdicion ? undefined : irAPaso} size="sm" mb="xl">
                <Stepper.Step label="Datos Generales" description="Proyecto y trabajo" />
                <Stepper.Step label="Repuestos" description="Repuestos y embalaje" />
                <Stepper.Step label="Mano de Obra y Costos" description="Horas, tiempos y gastos" />
                <Stepper.Step label="Precio y Resumen" description="Factor, carátula y costos" />
            </Stepper>

            <Box style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                {paso === 0 && <PasoDatosGeneralesVista cotizacion={cotizacion} errores={props.errores} alCambiar={props.alCambiar} />}
                {paso === 1 && <PasoRepuestosVista cotizacion={cotizacion} repuestos={props.repuestos} incoterms={props.incoterms} alCambiar={props.alCambiar} />}
                {paso === 2 && <PasoCostosVista cotizacion={cotizacion} conceptos={props.conceptos} errores={props.errores} alCambiar={props.alCambiar} />}
                {paso === 3 && <PasoPrecioVista cotizacion={cotizacion} incoterms={props.incoterms} errores={props.errores} requiereRecalculo={requiereRecalculo} alCambiar={props.alCambiar} />}
            </Box>

            <Group justify="space-between" mt="md">
                <Button variant="light" color="primary" size="xs" radius="lg" leftSection={<IconArrowLeft size={16} />} onClick={() => setPaso(p => p - 1)} disabled={paso === 0 || hayFilaEnEdicion}>
                    Atrás
                </Button>
                {paso < ULTIMO_PASO ? (
                    <Button size="xs" radius="xl" rightSection={<IconArrowRight size={16} />} onClick={() => irAPaso(paso + 1)} disabled={hayFilaEnEdicion}>
                        Siguiente
                    </Button>
                ) : (
                    <Group gap="xs">
                        <Button variant="light" size="xs" radius="xl" leftSection={<IconCalculator size={16} />} onClick={props.alCalcular}>
                            {cotizacion.Resultado ? 'Recalcular' : 'Calcular Cotización'}
                        </Button>
                        <Button variant="light" size="xs" radius="xl" leftSection={<IconFileTypePdf size={16} />} onClick={props.alGenerarPdf} disabled={sinResultado}>
                            Generar PDF
                        </Button>
                        <Button size="xs" radius="xl" leftSection={<IconDeviceFloppy size={16} />} onClick={props.alGuardar}>
                            Guardar
                        </Button>
                    </Group>
                )}
            </Group>
        </div>
    );
};
